import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pin, PinOff, Trash2, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import {
  useEvents, useEventNotes, useEventStaff, useCreateNote, useUpdateNote, useDeleteNote,
  CATEGORIES, CATEGORY_COLORS, formatCategory
} from "@/hooks/useChecklistsData";

const ALL_NOTE_CATS = [...CATEGORIES, "general"] as const;

const EventNotesTab: React.FC = () => {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [filterCat, setFilterCat] = useState<string>("all");
  const [noteForm, setNoteForm] = useState({ category: "general", note: "", is_pinned: false });

  const { data: events = [] } = useEvents();
  const { data: notes = [], isLoading } = useEventNotes(selectedEventId);
  const { data: staff = [] } = useEventStaff();

  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();

  const getStaffName = (id: string | null) => {
    if (!id) return "Unknown";
    return staff.find((s: any) => s.id === id)?.name || "Unknown";
  };

  const handleSubmit = () => {
    if (!selectedEventId || !noteForm.note.trim()) return;
    createNote.mutate({ event_id: selectedEventId, ...noteForm });
    setNoteForm({ category: "general", note: "", is_pinned: false });
  };

  const filteredNotes = filterCat === "all" ? notes : notes.filter((n: any) => n.category === filterCat);
  const pinnedNotes = filteredNotes.filter((n: any) => n.is_pinned);
  const unpinnedNotes = filteredNotes.filter((n: any) => !n.is_pinned);

  return (
    <div className="space-y-4">
      <Select value={selectedEventId || ""} onValueChange={v => setSelectedEventId(v)}>
        <SelectTrigger className="w-full max-w-md">
          <SelectValue placeholder="Select an event..." />
        </SelectTrigger>
        <SelectContent>
          {events.map((e: any) => (
            <SelectItem key={e.id} value={e.id}>{e.name} ({e.start_date})</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!selectedEventId && (
        <Card><CardContent className="py-8 text-center text-muted-foreground">Select an event to view notes.</CardContent></Card>
      )}

      {selectedEventId && (
        <>
          {/* Add note form */}
          <Card>
            <CardContent className="py-4 space-y-3">
              <div className="flex gap-2 flex-wrap">
                <Select value={noteForm.category} onValueChange={v => setNoteForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALL_NOTE_CATS.map(c => <SelectItem key={c} value={c}>{formatCategory(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Checkbox checked={noteForm.is_pinned} onCheckedChange={(v) => setNoteForm(p => ({ ...p, is_pinned: !!v }))} />
                  <Label className="text-sm">Pin this note</Label>
                </div>
              </div>
              <Textarea
                value={noteForm.note}
                onChange={e => setNoteForm(p => ({ ...p, note: e.target.value }))}
                placeholder="Add a note for the team..."
                rows={3}
              />
              <Button onClick={handleSubmit} disabled={!noteForm.note.trim()} className="gap-1">
                <MessageSquare className="h-4 w-4" /> Add Note
              </Button>
            </CardContent>
          </Card>

          {/* Filter */}
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Filter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {ALL_NOTE_CATS.map(c => <SelectItem key={c} value={c}>{formatCategory(c)}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Pinned notes */}
          {pinnedNotes.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-1"><Pin className="h-3 w-3" /> Pinned</h3>
              {pinnedNotes.map((note: any) => (
                <Card key={note.id} className="border-secondary/50 bg-secondary/5">
                  <CardContent className="py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={CATEGORY_COLORS[note.category] || CATEGORY_COLORS.other}>{formatCategory(note.category)}</Badge>
                        <span className="text-xs text-muted-foreground">{getStaffName(note.created_by)} · {format(new Date(note.created_at), "MMM d, h:mm a")}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => updateNote.mutate({ id: note.id, updates: { is_pinned: false } })}>
                          <PinOff className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteNote.mutate(note.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Regular notes */}
          <div className="space-y-2">
            {unpinnedNotes.length === 0 && pinnedNotes.length === 0 && (
              <Card><CardContent className="py-8 text-center text-muted-foreground">No notes yet for this event.</CardContent></Card>
            )}
            {unpinnedNotes.map((note: any) => (
              <Card key={note.id}>
                <CardContent className="py-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={CATEGORY_COLORS[note.category] || CATEGORY_COLORS.other}>{formatCategory(note.category)}</Badge>
                      <span className="text-xs text-muted-foreground">{getStaffName(note.created_by)} · {format(new Date(note.created_at), "MMM d, h:mm a")}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => updateNote.mutate({ id: note.id, updates: { is_pinned: true } })}>
                        <Pin className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteNote.mutate(note.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EventNotesTab;
